import { useEffect, useRef, useState } from 'react';
import {
  askQa,
  fetchLecture,
  fetchQaStatus,
  type QaAnswer,
  type QaSourceRef,
  type QaStatus,
} from '../api.js';

/**
 * 資料Q&A 画面。
 *
 * 講義ノート・動画要約・翻訳資料・週報を横断して質問できる。
 * NotebookLM と同じ考え方で、**答えより出典が主役**の画面にしてある:
 * 回答には出典カードが付き、元資料の名前・見出し・抜粋が読める。
 * 資料に無いことは答えない(サーバー側で担保)。資料の外の一般知識は
 * 「📖 資料外の補足」として**はっきり分けて**表示し、混同を防ぐ。
 *
 * 画面構成の方針(現地フィードバック):
 * 入力欄は**常に画面上部に固定**する。下に置くと、やり取りが増えるほど
 * 入力欄が押し下げられて打ちにくくなる。回答は時系列で下へ積み、
 * 質問すると最新の回答まで自動でスクロールする(過去は上へ戻れば読める)。
 *
 * リアルタイム翻訳とは独立した画面で、翻訳セッションには一切干渉しない。
 */

interface Turn {
  question: string;
  answer?: QaAnswer;
  error?: string;
}

/** 画面を離れて戻ってもやり取りが消えないようにする(戻る操作の誤爆対策)。 */
const STORAGE_KEY = 'cixing.docsQa.thread';

/** サーバー側の上限と合わせる。 */
const MAX_QUESTION_CHARS = 500;

function loadThread(): Turn[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Turn[]) : [];
  } catch {
    return [];
  }
}

const TYPE_LABELS: Record<string, string> = {
  'lecture-note': '講義ノート',
  'video-summary': '講義動画の要約',
  doc: '翻訳資料',
  'weekly-report': '週報',
};

export function DocsQa({ onBack }: { onBack: () => void }): JSX.Element {
  const [thread, setThread] = useState<Turn[]>(loadThread);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [status, setStatus] = useState<QaStatus>();
  const [openNote, setOpenNote] = useState<{ title: string; body: string }>();
  /** 最新のやり取りへスクロールするための目印。 */
  const latestRef = useRef<HTMLDivElement>(null);
  /** 固定した入力欄。スクロール量を実寸から決めるために参照する。 */
  const headerRef = useRef<HTMLDivElement>(null);
  /** 画面に戻ってきた直後は勝手に動かさない(自分で開いた位置を尊重する)。 */
  const firstRender = useRef(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fetchQaStatus()
      .then(setStatus)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(thread));
    } catch {
      // 保存できなくても表示には影響しない。
    }
  }, [thread]);

  /**
   * 最新のやり取りを、固定した入力欄のすぐ下に来る位置まで送る。
   *
   * **描画が終わってから実行すること。** 回答が入る前に呼ぶと、その時点の
   * 短いページ高さでスクロール上限に当たり、途中までしか進まない(実測)。
   * そのため呼び出しは thread の変化を見る useEffect に集約してある。
   * 余白はヘッダーの実寸から求める(固定値だと文字数表示や履歴ボタンの
   * 有無で高さが変わったときに隠れる)。
   */
  const scrollToLatest = (): void => {
    const target = latestRef.current;
    if (!target) return;
    const headerHeight = headerRef.current?.offsetHeight ?? 0;
    const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 12;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  /**
   * 入力量に合わせて高さを変える。
   *
   * 常に大きくしておくと、固定ヘッダーが画面の3割超を占めて回答が読みにくい(実測)。
   * 空のときは小さく、長い質問を打つ間だけ広げる。
   * 上限は画面の高さの25%(スマホ実測で約100字が一度に見える量)。
   * 端末が小さいほど上限も下がるので、狭い画面で入力欄が画面を埋めることはない。
   */
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const max = Math.max(120, Math.round(window.innerHeight * 0.25));
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 56), max)}px`;
  }, [question]);

  // やり取りが増えた/回答が届いたら、最新の位置まで送る。
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    scrollToLatest();
  }, [thread]);

  const ask = async (text: string): Promise<void> => {
    const trimmed = text.trim();
    if (trimmed === '' || asking) return;
    setAsking(true);
    setQuestion('');
    const index = thread.length;
    setThread((prev) => [...prev, { question: trimmed }]);

    // 追い質問の文脈は、回答が取れた往復だけを渡す。
    const history = thread
      .filter((t) => t.answer && !t.answer.noAnswer)
      .map((t) => ({ q: t.question, a: t.answer?.answer ?? '' }));

    try {
      const answer = await askQa(trimmed, history);
      setThread((prev) => prev.map((t, i) => (i === index ? { ...t, answer } : t)));
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : '回答を取得できませんでした。';
      setThread((prev) => prev.map((t, i) => (i === index ? { ...t, error: message } : t)));
    } finally {
      setAsking(false);
    }
  };

  const clearThread = (): void => {
    setThread([]);
    setQuestion('');
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // 消せなくても画面上は空になる。
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showLectureNote = async (source: QaSourceRef): Promise<void> => {
    if (!source.lectureId) return;
    setOpenNote({ title: source.sourceName, body: '読み込んでいます…' });
    try {
      const data = await fetchLecture(source.lectureId);
      setOpenNote({
        title: data.meta.title,
        body: data.note ?? 'この講義にはノートがありません。',
      });
    } catch {
      setOpenNote({ title: source.sourceName, body: '講義メモを開けませんでした。' });
    }
  };

  const summary = status
    ? Object.entries(status.byType)
        .map(([type, count]) => `${TYPE_LABELS[type] ?? type} ${count}`)
        .join(' / ')
    : '';

  return (
    <div className="screen">
      {/* 入力欄は常に上に固定する。やり取りが増えても打つ場所が動かない。 */}
      <div
        ref={headerRef}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'var(--bg)',
          paddingTop: 8,
          paddingBottom: 12,
          borderBottom: '1px solid var(--border)',
          marginBottom: 16,
        }}
      >
        <div className="row">
          <button onClick={onBack} style={{ flex: 'none' }}>
            ← 戻る
          </button>
          <h1 className="app-title" style={{ margin: 0, flex: 1 }}>
            資料に質問
          </h1>
          {thread.length > 0 && (
            <button onClick={clearThread} style={{ flex: 'none' }}>
              履歴を消す
            </button>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void ask(question);
          }}
        >
          <textarea
            ref={textareaRef}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="資料について質問する(例: 度目の調整はどうやりますか?)"
            maxLength={MAX_QUESTION_CHARS}
            rows={2}
            disabled={asking}
            style={{ marginTop: 10, resize: 'none', lineHeight: 1.6, overflowY: 'auto' }}
            onKeyDown={(e) => {
              // スマホでは Enter を改行に残す。PC 向けに Cmd/Ctrl+Enter で送信できるようにする。
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void ask(question);
              }
            }}
          />
          <div className="row" style={{ marginTop: 8, alignItems: 'center' }}>
            <span className="hint" style={{ flex: 1 }}>
              {question.length > 0
                ? `${question.length} / ${MAX_QUESTION_CHARS} 字`
                : summary !== ''
                  ? `収録: ${summary}`
                  : ''}
            </span>
            <button
              type="submit"
              className="primary"
              disabled={asking || question.trim() === ''}
              style={{ flex: 'none' }}
            >
              {asking ? '調べています…' : '質問する'}
            </button>
          </div>
        </form>
      </div>

      {status && !status.available && (
        <div className="notice error">
          資料Q&Aは現在利用できません。リアルタイム翻訳はそのまま使えます。
        </div>
      )}

      {thread.length === 0 && (
        <div className="empty">
          研修で分からなかったことを聞いてみてください。
          <br />
          資料に書かれていることは出典付きで答えます。
          <br />
          書かれていないことは「記載がありません」と返し、
          <br />
          一般的な知識は「📖 補足」として分けて表示します。
        </div>
      )}

      {thread.map((turn, i) => (
        <div
          key={i}
          ref={i === thread.length - 1 ? latestRef : undefined}
          style={{ marginBottom: 26 }}
        >
          {/*
            質問・補足・質問案はいずれも囲み枠になる。既定の .notice は左線が
            すべてオレンジで、3つが同じ見た目になり区別できなかった(現地指摘)。
            役割ごとに左線の色を変える: 質問=灰(自分の発言)/ 補足=オレンジ(注意)/
            質問案=緑(次の行動)。
          */}
          <div
            style={{
              background: 'var(--bg-raised)',
              border: '1px solid var(--border)',
              borderLeft: '3px solid var(--text-dim)',
              borderRadius: 8,
              padding: '12px 14px',
              fontWeight: 600,
              fontSize: 15,
              lineHeight: 1.6,
            }}
          >
            <span className="hint" style={{ display: 'block', marginBottom: 4, fontWeight: 400 }}>
              質問
            </span>
            {turn.question}
          </div>

          {turn.error && <div className="notice error">{turn.error}</div>}

          {!turn.answer && !turn.error && (
            <p className="hint" style={{ marginTop: 10 }}>
              資料を調べています…(十数秒かかることがあります)
            </p>
          )}

          {turn.answer && (
            <>
              {/* 「資料からの回答」と「資料外の補足」を言葉でも対比させ、取り違えを防ぐ。 */}
              <p className="hint" style={{ margin: '14px 0 4px' }}>
                {turn.answer.noAnswer ? '資料の検索結果' : '📗 資料からの回答'}
              </p>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{turn.answer.answer}</div>

              {turn.answer.supplement !== '' && (
                <div
                  className="notice"
                  style={{ marginTop: 12, borderLeft: '3px solid var(--accent, #ffb020)' }}
                >
                  <strong>📖 資料外の補足(一般的な知識)</strong>
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, marginTop: 6 }}>
                    {turn.answer.supplement}
                  </div>
                  <span className="hint" style={{ display: 'block', marginTop: 8 }}>
                    この部分は資料に基づかない一般知識です。実機・この機種の仕様については講師に確認してください。
                  </span>
                </div>
              )}

              {turn.answer.sources.length > 0 && (
                <>
                  <p className="hint" style={{ marginTop: 14, marginBottom: 6 }}>
                    出典({turn.answer.sources.length}件)
                  </p>
                  <div className="profile-list">
                    {turn.answer.sources.map((source) => (
                      <SourceCard key={source.chunkId} source={source} onOpenNote={showLectureNote} />
                    ))}
                  </div>
                </>
              )}

              {turn.answer.noAnswer && turn.answer.relatedSources.length > 0 && (
                <>
                  <p className="hint" style={{ marginTop: 14, marginBottom: 6 }}>
                    🔎 関連しそうな資料(回答の根拠ではありません。自分で確かめてください)
                  </p>
                  <div className="profile-list">
                    {turn.answer.relatedSources.map((source) => (
                      <SourceCard
                        key={source.chunkId}
                        source={source}
                        onOpenNote={showLectureNote}
                        showNumber={false}
                      />
                    ))}
                  </div>
                </>
              )}

              {turn.answer.suggestedQuestions.length > 0 && (
                <div
                  className="notice"
                  style={{ marginTop: 12, borderLeft: '3px solid var(--ok, #35c26b)' }}
                >
                  <strong>💬 講師に聞くとよい質問</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                    {turn.answer.suggestedQuestions.map((q, qi) => (
                      <li key={qi} style={{ marginTop: 4 }}>
                        {q}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      ))}

      {openNote && (
        <div
          onClick={() => setOpenNote(undefined)}
          style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,.45)' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              maxHeight: '80vh',
              overflowY: 'auto',
              background: 'var(--bg-raised, #151a21)',
              borderRadius: '14px 14px 0 0',
              padding: '16px 18px calc(18px + env(safe-area-inset-bottom))',
            }}
          >
            <div className="row" style={{ alignItems: 'baseline' }}>
              <h2 style={{ margin: 0, flex: 1, fontSize: 18 }}>{openNote.title}</h2>
              <button
                style={{ minHeight: 36, padding: '4px 12px', fontSize: 13 }}
                onClick={() => setOpenNote(undefined)}
              >
                閉じる
              </button>
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', marginTop: 12, fontSize: 15, lineHeight: 1.7 }}>
              {openNote.body}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

/** 出典カード。回答の根拠と「関連しそうな資料」で同じ見た目を使う。 */
function SourceCard({
  source,
  onOpenNote,
  showNumber = true,
}: {
  source: QaSourceRef;
  onOpenNote: (source: QaSourceRef) => Promise<void>;
  showNumber?: boolean;
}): JSX.Element {
  return (
    <div className="profile-item" style={{ display: 'block' }}>
      <strong>
        {showNumber ? `[${source.n}] ` : ''}
        {source.sourceName}
      </strong>
      <span className="badge" style={{ marginLeft: 6 }}>
        {TYPE_LABELS[source.sourceType] ?? source.sourceType}
      </span>
      {source.section !== '' && (
        <span className="hint" style={{ display: 'block', marginTop: 4 }}>
          {source.section}
        </span>
      )}
      <span className="hint" style={{ display: 'block', marginTop: 4 }}>
        {source.snippet}…
      </span>
      {source.lectureId && (
        <button style={{ marginTop: 8 }} onClick={() => void onOpenNote(source)}>
          講義ノートを開く
        </button>
      )}
    </div>
  );
}
